import { CalendarIcon } from '@heroicons/react/24/outline';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/utils/cn';

import { Calendar } from '../Calendar';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Props do componente DatePicker.
 */
export interface DatePickerProps {
  /** Label associado ao campo */
  readonly label?: string;
  /** ID do input */
  readonly id?: string;
  /** Placeholder exibido quando não há valor (default: DD/MM/AAAA) */
  readonly placeholder?: string;
  /** Valor em formato ISO (YYYY-MM-DD) */
  readonly value?: string;
  /** Callback com valor ISO (YYYY-MM-DD) ou string vazia ao limpar */
  readonly onChange?: (isoValue: string) => void;
  /** Desabilita o campo */
  readonly disabled?: boolean;
  /** Tamanho do campo */
  readonly size?: 'sm' | 'md' | 'lg';
  /** Exibe estado de erro */
  readonly hasError?: boolean;
  /** Mensagem auxiliar ou de erro */
  readonly helper?: string;
  /** Classe CSS adicional no wrapper */
  readonly className?: string;
  /** Lado de abertura do popover do calendário (default: "bottom") */
  readonly popoverSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Container onde o calendário é portado. Passe o elemento do modal (ex.:
   * `Dialog.Content`) quando o DatePicker é usado dentro de um Radix Dialog
   * modal — assim a seleção de data não conta como "interação externa" e não
   * fecha o modal. Quando omitido, o calendário é portado para `document.body`.
   */
  readonly portalContainer?: HTMLElement | null | undefined;
  /** Callback com o valor exibido no input (máscara DD/MM/AAAA), inclusive quando inválido */
  readonly onDisplayChange?: (display: string) => void;
  /** Callback disparado quando o input perde o foco, com o valor exibido atual */
  readonly onInputBlur?: (display: string) => void;
  /** Callback disparado quando o usuário completa 8 dígitos mas a data é inválida */
  readonly onInvalidInput?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isoToDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  const parsed = dayjs(iso, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.toDate() : undefined;
}

function dateToIso(date: Date | undefined): string {
  return date ? dayjs(date).format('YYYY-MM-DD') : '';
}

function isoToDisplay(iso: string | undefined): string {
  const date = isoToDate(iso);
  return date ? dayjs(date).format('DD/MM/YYYY') : '';
}

function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function displayToIso(display: string): string {
  const digits = display.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const parsed = dayjs(`${year}-${pad(month)}-${pad(day)}`, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const sizeStyles: Record<NonNullable<DatePickerProps['size']>, string> = {
  sm: 'h-[2rem] ts-paragraph-xs',
  md: 'h-[2.5rem] ts-paragraph-xs',
  lg: 'h-[3rem] ts-paragraph-xs',
};

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * DatePicker com calendário visual baseado em Radix UI Popover.
 *
 * Renderiza um input editável (máscara `DD/MM/AAAA`) acompanhado de um botão
 * que abre um popover com o `Calendar` para seleção de data. Aceita digitação
 * direta e seleção pelo calendário. É totalmente controlado via `value`
 * (formato ISO `YYYY-MM-DD`) e emite `onChange` com a nova data em ISO ou
 * string vazia quando o input está vazio/inválido.
 *
 * @example
 * ```tsx
 * <DatePicker
 *   label="Data de início"
 *   value={filters.startDate}
 *   onChange={iso => setFilter('startDate', iso)}
 * />
 * ```
 */
export function DatePicker({
  label,
  id,
  placeholder = 'DD/MM/AAAA',
  value,
  onChange,
  onDisplayChange,
  onInputBlur,
  onInvalidInput,
  disabled = false,
  size = 'md',
  hasError = false,
  helper,
  className,
  popoverSide = 'bottom',
  portalContainer,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState(() => isoToDisplay(value));
  const [visibleMonth, setVisibleMonth] = useState<Date | undefined>(() => isoToDate(value));

  const selectedDate = isoToDate(value);

  // Sincroniza o texto exibido quando o `value` muda por fora (ex.: reset do
  // formulário / edição). Feito em efeito (pós-commit) e não durante o render,
  // pois a mutação de ref no render é descartada pelo double-render do StrictMode
  // e faria o campo ficar vazio mesmo com valor. O guard por `lastIsoRef` evita
  // sobrescrever o que o usuário está digitando (mudanças internas ecoam o mesmo
  // `value` que acabamos de emitir).
  const lastIsoRef = useRef(value);
  useEffect(() => {
    if (value === lastIsoRef.current) return;
    lastIsoRef.current = value;
    setDisplayValue(isoToDisplay(value));
    const parsed = isoToDate(value);
    if (parsed) setVisibleMonth(parsed);
  }, [value]);

  const handleSelect = useCallback(
    (next: Date | undefined) => {
      const iso = dateToIso(next);
      lastIsoRef.current = iso;
      setDisplayValue(isoToDisplay(iso));
      if (next) setVisibleMonth(next);
      onChange?.(iso);
      setOpen(false);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyMask(e.target.value);
      setDisplayValue(masked);
      const iso = displayToIso(masked);
      lastIsoRef.current = iso;
      onChange?.(iso);
      if (iso) {
        const parsed = isoToDate(iso);
        if (parsed) setVisibleMonth(parsed);
      } else if (masked.replace(/\D/g, '').length === 8) {
        onInvalidInput?.();
      }
      onDisplayChange?.(masked);
    },
    [onChange, onInvalidInput, onDisplayChange],
  );

  return (
    <div className={cn('flex flex-col gap-quark', className)}>
      {label !== undefined && (
        <label htmlFor={id} className="ts-heading-xxs !font-medium text-ctx-content-title">
          {label}
        </label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            className={cn(
              'flex w-full items-center gap-xxs rounded-sm !border-hairline px-micro',
              'bg-base-neutral-none text-ctx-content-base',
              'transition focus-within:ring-[0.25rem] focus-within:ring-ctx-highlight-ringFocusShadow',
              hasError
                ? 'border-base-danger-500'
                : 'border-ctx-layout-border hover:bg-ctx-interactive-secondaryHover',
              disabled && 'cursor-not-allowed opacity-medium',
              sizeStyles[size],
            )}
          >
            <input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={placeholder}
              value={displayValue}
              onChange={handleInputChange}
              onBlur={() => onInputBlur?.(displayValue)}
              disabled={disabled}
              aria-invalid={hasError || undefined}
              className={cn(
                'min-w-none flex-1 bg-base-neutral-none text-ctx-content-base outline-none',
                'placeholder:text-ctx-content-placeholder',
                'disabled:cursor-not-allowed',
              )}
            />
            <PopoverTrigger
              type="button"
              disabled={disabled}
              aria-label="Abrir calendário"
              className={cn(
                'shrink-0 rounded-sm text-ctx-content-placeholder',
                'hover:text-ctx-content-base focus-visible:outline-none',
                'disabled:cursor-not-allowed',
              )}
            >
              <CalendarIcon className="size-mini" aria-hidden="true" />
            </PopoverTrigger>
          </div>
        </PopoverAnchor>

        <PopoverPortal {...(portalContainer ? { container: portalContainer } : {})}>
          <PopoverContent
            side={popoverSide}
            align="start"
            sideOffset={4}
            avoidCollisions={false}
            onOpenAutoFocus={e => e.preventDefault()}
            className="z-50 rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level3"
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              {...(visibleMonth ? { month: visibleMonth } : {})}
              onMonthChange={setVisibleMonth}
            />
          </PopoverContent>
        </PopoverPortal>
      </Popover>

      {helper !== undefined && (
        <span className={cn('ts-paragraph-xxs', 'text-ctx-content-placeholder')}>{helper}</span>
      )}
    </div>
  );
}
